import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Home, Brain, Users, Inbox, Bell, LogOut, Wrench, Megaphone,
  CalendarClock, MessageSquare, ScrollText, Plug, ChevronDown, Building2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import SupportAssistant from "@/components/SupportAssistant";
import GlobalSearch from "@/components/GlobalSearch";

const ROLE_PT = { rep: "Representante", manager: "Gestor", owner: "CEO", admin: "Admin", superadmin: "Superadmin", operador: "Operador" };

function PendingBell({ count }) {
  const nav = useNavigate();
  return (
    <button data-testid="pending-bell" onClick={() => nav("/app/pending")} title="Perguntas pendentes para a ISA"
      className="relative rounded-full p-2 text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-900">
      <Bell size={18} strokeWidth={1.75} />
      {count > 0 && (
        <span data-testid="pending-bell-badge"
          className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

const linkClass = ({ isActive }) =>
  `flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-[background-color,color] duration-200 ${
    isActive ? "bg-[#0b1220] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

export default function DashboardLayout() {
  const { user, tenant, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const isMgr = ["manager", "owner", "admin", "superadmin"].includes(user?.role);
  const isOperator = user?.role === "operador";
  const canOperate = isMgr || isOperator;
  const canIntegrations = ["admin", "owner"].includes(user?.role);
  const canBroadcast = ["manager", "owner", "admin", "operador"].includes(user?.role) || (user?.permissions || []).includes("broadcast.send");
  const canPending = isMgr || (user?.permissions || []).includes("pending_questions.view");

  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!canPending) return;
    const load = () => api.get("/pending/count").then(({ data }) => setPendingCount(data.count || 0)).catch(() => {});
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [canPending]);

  const [menu, setMenu] = useState(null); // 'tools' | 'user' | 'mobile' | null
  const barRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setMenu(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setMenu(null); }, [location.pathname]);

  const primary = [
    { to: "/app", icon: Home, label: "Início", end: true, tid: "nav-inicio" },
    ...(canOperate ? [{ to: "/app/brain", icon: Brain, label: "Cérebro", tid: "nav-cerebro" }] : []),
    ...(canOperate ? [{ to: "/app/team", icon: Users, label: "Pessoas", tid: "nav-pessoas" }] : []),
    ...(canPending ? [{ to: "/app/pending", icon: Inbox, label: "Pendências", tid: "nav-pendencias", alert: true }] : []),
  ];
  // Setores: menu no lugar do antigo "Comercial". Comercial está pronto; os demais são placeholders.
  const sectors = isMgr ? [
    { to: "/app/comercial", label: "Comercial", tid: "nav-comercial", ready: true },
    { to: "/app/setor/financeiro", label: "Financeiro", tid: "nav-setor-financeiro" },
    { to: "/app/setor/rh", label: "RH", tid: "nav-setor-rh" },
    { to: "/app/setor/juridico", label: "Jurídico", tid: "nav-setor-juridico" },
    { to: "/app/setor/operacoes", label: "Operações", tid: "nav-setor-operacoes" },
    { to: "/app/setor/marketing", label: "Marketing", tid: "nav-setor-marketing" },
    { to: "/app/setor/administrativo", label: "Administrativo", tid: "nav-setor-administrativo" },
  ] : [];
  const campaignsItem = canBroadcast ? { to: "/app/campaigns", icon: Megaphone, label: "Campanhas", tid: "nav-campanhas" } : null;
  const isSectorActive = /^\/app\/(comercial|setor|licitacoes)/.test(location.pathname);
  const secondary = [
    ...(isMgr ? [{ to: "/app/agenda", icon: CalendarClock, label: "Agenda", tid: "nav-agenda" }] : []),
    ...(canOperate ? [{ to: "/app/simulator", icon: MessageSquare, label: "Simulador WhatsApp", tid: "nav-simulator" }] : []),
    ...(isMgr ? [{ to: "/app/audit", icon: ScrollText, label: "Auditoria", tid: "nav-audit" }] : []),
    ...(canIntegrations ? [{ to: "/app/integrations", icon: Plug, label: "Integrações", tid: "nav-integrations" }] : []),
  ];
  const allItems = [...primary, ...sectors.map((s) => ({ ...s, icon: Building2 })), ...(campaignsItem ? [campaignsItem] : []), ...secondary];
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* floating top nav */}
      <div ref={barRef} className="sticky top-4 z-50 px-3">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 rounded-full border border-slate-200/70 bg-white/80 px-3 py-2 shadow-[0_6px_28px_rgb(11,18,32,0.05)] backdrop-blur-xl lg:px-5">
          <div className="flex shrink-0 items-center gap-1">
            <NavLink to="/app" className="mr-1 flex shrink-0 items-center px-2" data-testid="brand-logo">
              <img src="/asiisa-logo.png" alt="ASIISA" className="h-5 w-auto shrink-0" />
            </NavLink>
          </div>

          {/* desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {primary.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid} className={linkClass}>
                <it.icon size={16} strokeWidth={1.75} /> {it.label}
                {it.alert && pendingCount > 0 && (
                  <span data-testid="nav-pendencias-badge"
                    className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </NavLink>
            ))}
            {!!sectors.length && (
              <div className="relative">
                <button data-testid="nav-setores" onClick={() => setMenu(menu === "sectors" ? null : "sectors")}
                  className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${(menu === "sectors" || isSectorActive) ? "bg-[#0b1220] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                  <Building2 size={16} strokeWidth={1.75} /> Setores <ChevronDown size={14} className={`transition-transform ${menu === "sectors" ? "rotate-180" : ""}`} />
                </button>
                {menu === "sectors" && (
                  <div data-testid="sectors-menu" className="absolute left-0 top-12 w-60 overflow-hidden rounded-2xl border border-slate-100 bg-white p-1.5 shadow-[0_20px_50px_-12px_rgba(11,18,32,0.2)]">
                    {sectors.map((s) => (
                      <NavLink key={s.to} to={s.to} data-testid={s.tid}
                        className={({ isActive }) => `flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                        <span className="flex items-center gap-3"><Building2 size={16} strokeWidth={1.75} /> {s.label}</span>
                        {!s.ready && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400">Em breve</span>}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
            {campaignsItem && (
              <NavLink to={campaignsItem.to} data-testid={campaignsItem.tid} className={linkClass}>
                <campaignsItem.icon size={16} strokeWidth={1.75} /> {campaignsItem.label}
              </NavLink>
            )}
            {!!secondary.length && (
              <div className="relative">
                <button data-testid="nav-settings" onClick={() => setMenu(menu === "tools" ? null : "tools")}
                  className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${menu === "tools" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                  <Wrench size={16} strokeWidth={1.75} /> Ferramentas <ChevronDown size={14} className={`transition-transform ${menu === "tools" ? "rotate-180" : ""}`} />
                </button>
                {menu === "tools" && (
                  <div data-testid="settings-menu" className="absolute right-0 top-12 w-60 overflow-hidden rounded-2xl border border-slate-100 bg-white p-1.5 shadow-[0_20px_50px_-12px_rgba(11,18,32,0.2)]">
                    {secondary.map((it) => (
                      <NavLink key={it.to} to={it.to} data-testid={it.tid}
                        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                        <it.icon size={16} strokeWidth={1.75} /> {it.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* right cluster */}
          <div className="flex items-center gap-1.5">
            <GlobalSearch canOperate={canOperate} canPending={canPending} />
            {canPending && <PendingBell count={pendingCount} />}
            <div className="relative">
              <button data-testid="user-menu" onClick={() => setMenu(menu === "user" ? null : "user")}
                className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors duration-200 hover:bg-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0b1220] text-sm font-semibold text-white">{initial}</span>
                <span className="hidden min-w-0 flex-col justify-center text-left sm:flex">
                  <span className="max-w-[140px] truncate font-display text-sm font-semibold leading-tight text-slate-900">{user?.name}</span>
                  <span className="max-w-[140px] truncate text-[11px] leading-tight text-slate-400">{ROLE_PT[user?.role] || user?.role}</span>
                </span>
              </button>
              {menu === "user" && (
                <div className="absolute right-0 top-14 w-64 overflow-hidden rounded-2xl border border-slate-100 bg-white p-1.5 shadow-[0_20px_50px_-12px_rgba(11,18,32,0.2)]">
                  <div className="px-3 py-2.5" data-testid="company-name">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Empresa</p>
                    <p className="font-display text-sm font-semibold text-slate-900">{tenant?.name}</p>
                  </div>
                  <div className="my-1 border-t border-slate-100" />
                  <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors duration-150 hover:bg-red-50 hover:text-red-600">
                    <LogOut size={16} strokeWidth={1.75} /> {t("logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* mobile nav row */}
        <div className="mx-auto mt-2 flex max-w-7xl gap-1 overflow-x-auto rounded-full border border-slate-200/70 bg-white/80 px-2 py-1.5 backdrop-blur-xl lg:hidden">
          {allItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`m-${it.tid}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${isActive ? "bg-[#0b1220] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              <it.icon size={14} strokeWidth={1.75} /> {it.label}
              {it.alert && pendingCount > 0 && (
                <span data-testid="m-nav-pendencias-badge"
                  className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8 fade-up">
        <Outlet />
      </main>
      <SupportAssistant />
    </div>
  );
}
